import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { selectStylesMulti } from '../selectStyles';
import { IconClose } from '../icons';
import { SLOT_LABELS, MAX_SONGS } from './queryOps';
import ChipConnector from './ChipConnector';
import { normText, plural, stopEvent } from './util';
import './search.css';

const MAX_OPTIONS = 80;

const styles = {
  ...selectStylesMulti,
  valueContainer: (base, state) => ({ ...selectStylesMulti.valueContainer(base, state), rowGap: 4 }),
  menu: (base, state) => ({ ...selectStylesMulti.menu(base, state), zIndex: 1000 }),
};

// Catalog rows -> searchable entries (normalized once per catalog).
const indexSongs = (options) =>
  (Array.isArray(options) ? options : [])
    .filter((o) => o && o.key)
    .map((o) => ({
      key: o.key,
      name: o.name || o.key,
      plays: Number(o.plays) || 0,
      norm: normText(o.name || o.key),
      variants: (Array.isArray(o.variants) ? o.variants : [])
        .filter((v) => v && v !== o.name)
        .map((v) => ({ text: v, norm: normText(v) })),
    }));

const scoreText = (norm, t, words) => {
  if (!norm) return 0;
  if (norm === t) return 5;
  if (norm.startsWith(t)) return 4;
  const parts = norm.split(' ');
  if (words.every((w) => parts.some((p) => p.startsWith(w)))) return 3;
  if (words.every((w) => norm.indexOf(w) >= 0)) return 2;
  return 0;
};

// Rank catalog songs for the typed text: name matches before variant-only
// matches, then most played first. Empty text lists the most played songs.
const rankSongs = (entries, input, taken) => {
  const t = normText(input);
  const words = t ? t.split(' ') : [];
  const hits = [];
  entries.forEach((e) => {
    if (taken[e.key]) return;
    if (!words.length) {
      hits.push({ e, score: 0, via: null });
      return;
    }
    const s = scoreText(e.norm, t, words);
    if (s > 0) {
      hits.push({ e, score: s * 2, via: null });
      return;
    }
    let best = 0;
    let via = null;
    e.variants.forEach((v) => {
      const vs = scoreText(v.norm, t, words);
      if (vs > best) {
        best = vs;
        via = v.text;
      }
    });
    if (best > 0) hits.push({ e, score: best, via });
  });
  hits.sort((a, b) => b.score - a.score || b.e.plays - a.e.plays || a.e.name.localeCompare(b.e.name));
  return hits.slice(0, MAX_OPTIONS).map(({ e, via }) => ({ value: e.key, label: e.name, key: e.key, name: e.name, plays: e.plays, via }));
};

const formatOptionLabel = (o, { context }) =>
  context === 'menu' ? (
    <div className="ss-opt">
      <span className="ss-opt-main">
        <span className="ss-opt-name">{o.name}</span>
        {o.via && <span className="ss-opt-via">Listed as {o.via}</span>}
      </span>
      <span className="ss-opt-plays">{plural(o.plays, 'play')}</span>
    </div>
  ) : (
    o.label
  );

const chipTags = (s) => {
  const tags = [];
  if (s.slot && s.slot !== 'any') tags.push({ k: 'slot', t: SLOT_LABELS[s.slot], title: SLOT_LABELS[s.slot] });
  if (s.jam) {
    tags.push({
      k: 'jam',
      t: 'Long',
      title: s.jam === 90 ? 'Top 10%: longer than 9 in 10 versions' : 'Top 25%: longer than 3 in 4 versions',
    });
  }
  return tags;
};

// One chip inside the react-select control, with the connector to the chip
// before it. Module-level so react-select never remounts it (open menus keep
// their state); everything dynamic comes in through selectProps.chipApi.
const ChipValue = (props) => {
  const { data, selectProps, removeProps, isFocused } = props;
  const chipApi = selectProps.chipApi || {};
  const i = data.idx;
  const s = data.song;
  const links = chipApi.links || [];
  const content = (
    <span className="ss-chip-body">
      <span className="ss-chip-name" title={s.name}>
        {s.name}
      </span>
      {chipTags(s).map((t) => (
        <span key={t.k} className="ss-chip-tag" title={t.title}>
          {t.t}
        </span>
      ))}
    </span>
  );
  const move = (dir) => chipApi.move && chipApi.move(i, dir);
  const blocked = i > 0 && chipApi.linkBlocked ? chipApi.linkBlocked(i - 1) : null;
  return (
    <span className="ss-chipwrap">
      {i > 0 && chipApi.onToggleLink && (
        <ChipConnector
          linked={links[i - 1] === true}
          onToggle={() => chipApi.onToggleLink(i - 1)}
          disabled={!!blocked}
          disabledReason={blocked || undefined}
        />
      )}
      <span className={'ss-chip' + (isFocused ? ' focused' : '')} onMouseDown={stopEvent} onTouchEnd={stopEvent} onKeyDown={stopEvent}>
        {chipApi.renderChipMenu ? chipApi.renderChipMenu(s, i, content, { move, count: chipApi.count }) : content}
        <button
          type="button"
          className="ss-chip-x"
          aria-label={'Remove ' + s.name}
          title="Remove"
          onMouseDown={removeProps && removeProps.onMouseDown}
          onTouchEnd={removeProps && removeProps.onTouchEnd}
          onClick={removeProps && removeProps.onClick}
        >
          <IconClose size={11} />
        </button>
      </span>
    </span>
  );
};

const COMPONENTS = { MultiValue: ChipValue };

// Song chips (contract D.4.1): react-select 3.2 isMulti, controlled,
// closeMenuOnSelect false. Options come from the catalog (fetchCatalogSongs
// rows), ranked by the typed text, matching setlist.fm variants too, and
// showing play counts.
//   onChange(nextSongs, change) where change is one of
//     { action: 'add', song: { key, name } }
//     { action: 'remove', index }
//     { action: 'move', index, dir }      (-1 left, +1 right)
//     { action: 'clear' }
// Optional (additive) props used by SetSearchPanel: links, onToggleLink(i),
// linkBlocked(i) -> reason|null, renderChipMenu(song, index, content,
// { move, count }), placeholder, isLoading.
export default function SongChipInput({ options, value, onChange, links, onToggleLink, linkBlocked, renderChipMenu, placeholder, isLoading }) {
  const [input, setInput] = useState('');
  const songs = useMemo(() => (Array.isArray(value) ? value.filter((s) => s && s.key) : []), [value]);
  const entries = useMemo(() => indexSongs(options), [options]);
  const full = songs.length >= MAX_SONGS;
  const taken = useMemo(() => {
    const m = {};
    songs.forEach((s) => {
      m[s.key] = true;
    });
    return m;
  }, [songs]);
  const shown = useMemo(() => (full ? [] : rankSongs(entries, input, taken)), [entries, input, taken, full]);
  const selectValue = useMemo(() => songs.map((s, idx) => ({ value: s.key, label: s.name, idx, song: s })), [songs]);

  const emit = (next, change) => onChange && onChange(next, change);
  const move = (index, dir) => {
    const j = index + (dir < 0 ? -1 : 1);
    if (index < 0 || j < 0 || j >= songs.length) return;
    const next = songs.slice();
    next[index] = songs[j];
    next[j] = songs[index];
    emit(next, { action: 'move', index, dir: dir < 0 ? -1 : 1 });
  };

  const handle = (_next, meta) => {
    const action = meta && meta.action;
    if (action === 'select-option' && meta.option && !full) {
      const song = { key: meta.option.key, name: meta.option.name };
      emit(songs.concat([{ ...song, slot: 'any', jam: null }]), { action: 'add', song });
    } else if ((action === 'remove-value' || action === 'pop-value') && meta.removedValue) {
      const index = meta.removedValue.idx;
      if (index >= 0 && index < songs.length) emit(songs.filter((_, k) => k !== index), { action: 'remove', index });
    } else if (action === 'clear') {
      emit([], { action: 'clear' });
    }
  };

  const chipApi = { links: links || [], onToggleLink, linkBlocked, renderChipMenu, move, count: songs.length };

  return (
    <div className="ss-songs">
      <Select
        isMulti
        inputId="ss-song-input"
        classNamePrefix="ss-rs"
        aria-label="Songs"
        closeMenuOnSelect={false}
        isClearable={false}
        backspaceRemovesValue
        value={selectValue}
        options={shown}
        filterOption={null}
        inputValue={input}
        onInputChange={(v, m) => setInput(m && m.action === 'input-change' ? v : '')}
        onChange={handle}
        formatOptionLabel={formatOptionLabel}
        styles={styles}
        components={COMPONENTS}
        chipApi={chipApi}
        isLoading={!!isLoading}
        loadingMessage={() => 'Loading songs...'}
        noOptionsMessage={() =>
          full ? 'Up to ' + MAX_SONGS + ' songs in one search.' : input ? 'No song by that name.' : 'No songs to add.'
        }
        placeholder={placeholder || (songs.length ? 'Add another song...' : 'Type a song name...')}
        maxMenuHeight={300}
        menuShouldScrollIntoView
      />
    </div>
  );
}
